import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import AdsClickRoundedIcon from '@mui/icons-material/AdsClickRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { shortLinkAPI, adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader } from '../components/ui';

const DOMAIN_NAME = 'invcbl.in';

function generateRandomSlug(len = 6) {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let res = '';
  for (let i = 0; i < len; i++) {
    res += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return res;
}

function isPdfUrl(url) {
  if (!url) return false;
  return /\.pdf($|\?)/i.test(url) || url.toLowerCase().includes('/pdfs/');
}

const ShortLinks = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', search: '' });
  const page = parseInt(query.page, 10) || 1;
  const limit = parseInt(query.limit, 10) || 20;
  const search = query.search || '';
  const { permissions = [], can } = useAuth();
  const canManage = Boolean(permissions.includes('*') || permissions.includes('short_links_manage') || can?.('short_links_manage'));

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [targetType, setTargetType] = useState('url'); // 'url' | 'pdf'
  const [formTitle, setFormTitle] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formTargetUrl, setFormTargetUrl] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formError, setFormError] = useState('');

  // PDF upload state
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [uploadedPdfName, setUploadedPdfName] = useState('');

  // Delete dialog state
  const [deleteId, setDeleteId] = useState(null);

  // Toast / feedback state
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [copiedId, setCopiedId] = useState(null);

  // Fetch short links
  const { data: responseData, isLoading, isFetching } = useQuery(
    ['short-links', page, limit, search],
    async () => {
      const res = await shortLinkAPI.list({ page, limit, search });
      return res.data?.data || { items: [], pagination: { total: 0, totalPages: 1 } };
    },
    { keepPreviousData: true }
  );

  const items = responseData?.items || [];
  const pagination = responseData?.pagination || { total: 0, totalPages: 1 };
  const totalClicks = items.reduce((acc, curr) => acc + (curr.clicksCount || 0), 0);
  const activeCount = items.filter((item) => item.isActive).length;

  // Mutations
  const createMutation = useMutation((payload) => shortLinkAPI.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('short-links');
      setToast({ open: true, message: 'Short link created successfully!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (err) => {
      setFormError(err.response?.data?.message || err.message || 'Failed to create short link.');
    }
  });

  const updateMutation = useMutation(({ id, payload }) => shortLinkAPI.update(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('short-links');
      setToast({ open: true, message: 'Short link updated successfully!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (err) => {
      setFormError(err.response?.data?.message || err.message || 'Failed to update short link.');
    }
  });

  const deleteMutation = useMutation((id) => shortLinkAPI.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('short-links');
      setToast({ open: true, message: 'Short link deleted successfully!', severity: 'info' });
      setDeleteId(null);
    },
    onError: (err) => {
      setToast({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to delete short link.',
        severity: 'error'
      });
      setDeleteId(null);
    }
  });

  const handleOpenCreate = () => {
    if (!canManage) {
      setToast({
        open: true,
        message: 'You have view-only access. Short Links – Manage permission is required to create short links.',
        severity: 'info'
      });
      return;
    }
    setEditingItem(null);
    setTargetType('url');
    setFormTitle('');
    setFormSlug('');
    setFormTargetUrl('');
    setUploadedPdfName('');
    setFormIsActive(true);
    setFormError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    if (!canManage) {
      setToast({
        open: true,
        message: 'You have view-only access. Short Links – Manage permission is required to edit short links.',
        severity: 'info'
      });
      return;
    }
    setEditingItem(item);
    const isPdf = isPdfUrl(item.targetUrl);
    setTargetType(isPdf ? 'pdf' : 'url');
    setFormTitle(item.title || '');
    setFormSlug(item.slug || '');
    setFormTargetUrl(item.targetUrl || '');
    setUploadedPdfName(isPdf ? item.title || 'Attached PDF Document' : '');
    setFormIsActive(item.isActive !== false);
    setFormError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormError('');
    setIsUploadingPdf(false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      setFormError('Please select a valid PDF file.');
      return;
    }

    if (file.size > 35 * 1024 * 1024) {
      setFormError('PDF file is too large (Maximum allowed size: 35MB).');
      return;
    }

    setIsUploadingPdf(true);
    setFormError('');

    try {
      const cleanBaseName = file.name.replace(/\.pdf$/i, '');
      const res = await adminAPI.uploadPdf(file, { name: cleanBaseName });
      const cdnUrl = res.data?.data?.url;

      if (!cdnUrl) {
        throw new Error('Upload succeeded but no URL was returned.');
      }

      setFormTargetUrl(cdnUrl);
      setUploadedPdfName(file.name);

      // Auto-populate Title if empty
      if (!formTitle.trim()) {
        const prettyTitle = cleanBaseName.replace(/[-_]+/g, ' ');
        setFormTitle(prettyTitle.charAt(0).toUpperCase() + prettyTitle.slice(1));
      }

      // Auto-populate Slug if empty
      if (!formSlug.trim()) {
        const autoSlug = cleanBaseName
          .toLowerCase()
          .replace(/[^a-z0-9_-]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        setFormSlug(autoSlug);
      }

      setToast({ open: true, message: 'PDF uploaded to storage successfully!', severity: 'success' });
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Failed to upload PDF.');
    } finally {
      setIsUploadingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formTargetUrl.trim()) {
      setFormError(
        targetType === 'pdf'
          ? 'Please upload a PDF file before creating the link.'
          : 'Please enter a destination / target URL.'
      );
      return;
    }

    const payload = {
      title: formTitle.trim() || undefined,
      slug: formSlug.trim() || undefined,
      targetUrl: formTargetUrl.trim(),
      isActive: formIsActive
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleToggleActive = (item) => {
    if (!canManage) {
      setToast({
        open: true,
        message: 'You have view-only access. Short Links – Manage permission is required to change status.',
        severity: 'info'
      });
      return;
    }
    updateMutation.mutate({
      id: item.id,
      payload: { isActive: !item.isActive }
    });
  };

  const handleCopy = (slug, id) => {
    const fullUrl = `https://${DOMAIN_NAME}/${slug}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(id);
    setToast({ open: true, message: `Copied: ${fullUrl}`, severity: 'success' });
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Short Links Manager"
        subtitle={`Create and manage fast branded redirects on ${DOMAIN_NAME}`}
        actions={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleOpenCreate}
              sx={{
                bgcolor: 'var(--primary-color, #de5824)',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2.5,
                px: 2.5,
                '&:hover': { bgcolor: '#c2410c' }
              }}
            >
              Create Short Link
            </Button>
          ) : (
            <Tooltip title="View-only access. Short Links – Manage permission required.">
              <span>
                <Button
                  variant="contained"
                  disabled
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2.5,
                    px: 2.5
                  }}
                >
                  Create Short Link
                </Button>
              </span>
            </Tooltip>
          )
        }
      />

      {!canManage && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          You currently have <strong>view-only access</strong>. To create, edit, or delete short links, ask a Super Admin to assign the <strong>Short Links – Manage</strong> permission to your role in <strong>Role Builder</strong>.
        </Alert>
      )}

      {/* Top Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(222,88,36,0.1)',
                color: 'var(--primary-color, #de5824)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LinkRoundedIcon />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Total Short Links
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {pagination.total}
              </Typography>
            </Box>
          </Box>
        </Card>

        <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(16,185,129,0.1)',
                color: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircleOutlineRoundedIcon />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Active Links
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {activeCount}
              </Typography>
            </Box>
          </Box>
        </Card>

        <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(14,165,233,0.1)',
                color: '#0ea5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AdsClickRoundedIcon />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Total Redirect Clicks
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {totalClicks}
              </Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Main Table Card */}
      <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
        {/* Search bar toolbar */}
        <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by alias, title, or destination URL..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
            sx={{ width: { xs: '100%', sm: 380 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#94a3b8' }} fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          {isFetching && <CircularProgress size={20} sx={{ color: 'var(--primary-color, #de5824)' }} />}
        </Box>

        {/* Table */}
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Short Link ({DOMAIN_NAME})</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Destination URL</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', textAlign: 'center' }}>Clicks</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', textAlign: 'center' }}>Active</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={32} sx={{ color: 'var(--primary-color, #de5824)' }} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: '#64748b' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      No short links found.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Click &ldquo;Create Short Link&rdquo; to create your first branded URL on {DOMAIN_NAME}.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const isCopied = copiedId === item.id;
                  const isPdf = isPdfUrl(item.targetUrl);
                  const dateStr = item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })
                    : '-';

                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 800,
                              color: 'var(--primary-color, #de5824)',
                              fontFamily: 'monospace',
                              fontSize: '0.95rem'
                            }}
                          >
                            {DOMAIN_NAME}/{item.slug}
                          </Typography>
                          <Tooltip title={isCopied ? 'Copied!' : 'Copy Short Link'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(item.slug, item.id)}
                              sx={{
                                color: isCopied ? '#10b981' : '#64748b',
                                bgcolor: isCopied ? 'rgba(16,185,129,0.1)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(222,88,36,0.1)', color: 'var(--primary-color, #de5824)' }
                              }}
                            >
                              {isCopied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {item.title && (
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                            {item.title}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 380 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {isPdf && (
                            <Chip
                              size="small"
                              icon={<PictureAsPdfRoundedIcon sx={{ fontSize: '13px !important' }} />}
                              label="PDF"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 800,
                                bgcolor: '#fef2f2',
                                color: '#dc2626',
                                border: '1px solid #fecaca',
                                '& .MuiChip-icon': { color: '#dc2626' }
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: '#334155',
                              maxWidth: 300,
                              textDecoration: 'none'
                            }}
                          >
                            {item.targetUrl}
                          </Typography>
                          <Tooltip title={isPdf ? 'Open PDF Document' : 'Open Destination URL'}>
                            <IconButton
                              size="small"
                              component="a"
                              href={item.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a' } }}
                            >
                              <OpenInNewRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          size="small"
                          label={item.clicksCount || 0}
                          sx={{
                            fontWeight: 700,
                            bgcolor: item.clicksCount > 0 ? '#f0fdf4' : '#f8fafc',
                            color: item.clicksCount > 0 ? '#166534' : '#64748b',
                            border: '1px solid',
                            borderColor: item.clicksCount > 0 ? '#bbf7d0' : '#e2e8f0'
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title={!canManage ? 'Short Links – Manage permission required' : (item.isActive ? 'Deactivate link' : 'Activate link')}>
                          <span>
                            <Switch
                              checked={item.isActive}
                              onChange={() => handleToggleActive(item)}
                              size="small"
                              color="primary"
                              disabled={!canManage}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {dateStr}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: 'right' }}>
                        {canManage ? (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenEdit(item)} sx={{ color: '#64748b' }}>
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteId(item.id)}
                                sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            View Only
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <form onSubmit={handleFormSubmit}>
          <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
            {editingItem ? 'Edit Short Link' : 'Create New Short Link'}
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            {formError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {formError}
              </Alert>
            )}

            {/* Target Type Selector: Web URL vs Upload PDF */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
                Link Destination Type
              </Typography>
              <ToggleButtonGroup
                value={targetType}
                exclusive
                onChange={(e, val) => {
                  if (val) {
                    setTargetType(val);
                    setFormError('');
                  }
                }}
                fullWidth
                size="small"
                sx={{
                  bgcolor: '#f8fafc',
                  p: 0.5,
                  borderRadius: 2.5,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1,
                    '&.Mui-selected': {
                      bgcolor: '#ffffff',
                      color: 'var(--primary-color, #de5824)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }
                  }
                }}
              >
                <ToggleButton value="url">
                  <LanguageRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                  Web Page URL
                </ToggleButton>
                <ToggleButton value="pdf">
                  <PictureAsPdfRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                  Upload PDF Document
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Destination Content: Web URL Field OR PDF Upload Area */}
            {targetType === 'url' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75 }}>
                  Destination / Target URL *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://invinciblengo.org/events/kedarkantha-snow-trek-uttrakhand"
                  value={formTargetUrl}
                  onChange={(e) => setFormTargetUrl(e.target.value)}
                  required
                />
                <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                  Where should people be sent when they visit this short link?
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75 }}>
                  Upload PDF Document *
                </Typography>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handlePdfUpload}
                />

                {isUploadingPdf ? (
                  <Box
                    sx={{
                      p: 3,
                      border: '2px dashed var(--primary-color, #de5824)',
                      borderRadius: 2.5,
                      textAlign: 'center',
                      bgcolor: 'rgba(222,88,36,0.04)'
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: 'var(--primary-color, #de5824)', mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Uploading PDF to cloud storage...
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Please wait a moment.
                    </Typography>
                  </Box>
                ) : formTargetUrl && isPdfUrl(formTargetUrl) ? (
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      borderColor: '#fecaca',
                      bgcolor: '#fff5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: '#fee2e2',
                          color: '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <PictureAsPdfRoundedIcon />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {uploadedPdfName || 'PDF Document Ready'}
                        </Typography>
                        <Typography
                          variant="caption"
                          noWrap
                          component="a"
                          href={formTargetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#dc2626', textDecoration: 'underline', display: 'block' }}
                        >
                          View / Test Uploaded PDF
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        borderColor: '#fca5a5',
                        color: '#dc2626',
                        whiteSpace: 'nowrap',
                        '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' }
                      }}
                    >
                      Replace PDF
                    </Button>
                  </Card>
                ) : (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      p: 3,
                      border: '2px dashed #cbd5e1',
                      borderRadius: 2.5,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      bgcolor: '#f8fafc',
                      '&:hover': {
                        borderColor: 'var(--primary-color, #de5824)',
                        bgcolor: 'rgba(222,88,36,0.02)'
                      }
                    }}
                  >
                    <UploadFileRoundedIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 0.75 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Click to choose and upload a PDF
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                      Brochures, itineraries, circulars, or schedules (PDF up to 35MB)
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Custom Alias / Slug */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                  Custom Alias / Slug
                </Typography>
                <Button
                  size="small"
                  startIcon={<AutorenewRoundedIcon fontSize="inherit" />}
                  onClick={() => setFormSlug(generateRandomSlug(6))}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                >
                  Random Code
                </Button>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="kedarkantha-brochure"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {DOMAIN_NAME}/
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                Preview:{' '}
                <strong style={{ color: 'var(--primary-color, #de5824)' }}>
                  https://{DOMAIN_NAME}/{formSlug || 'your-alias'}
                </strong>{' '}
                (Leave blank to auto-generate a random 6-character code)
              </Typography>
            </Box>

            {/* Campaign Title / Notes */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75 }}>
                Campaign Title / Note (Optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Kedarkantha Winter Trek Official Brochure"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </Box>

            {/* Active Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                  Link Status
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  When inactive, visitors are redirected to the homepage.
                </Typography>
              </Box>
              <Switch checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} color="primary" />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUploadingPdf || createMutation.isLoading || updateMutation.isLoading}
              sx={{
                bgcolor: 'var(--primary-color, #de5824)',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                '&:hover': { bgcolor: '#c2410c' }
              }}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : editingItem ? (
                'Save Changes'
              ) : (
                'Create Link'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Are you sure you want to delete this short link? Any visitor who clicks it will no longer be redirected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate(deleteId)}
            disabled={deleteMutation.isLoading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {deleteMutation.isLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShortLinks;
